import { useEffect, useRef, useState } from "react";
import { useGetOfficeAppliances } from "../hooks/useGetOfficeAppliance";
import { PERMISSIONS } from "modules/PermissionsConstants";
import { useRecoilState, useRecoilValue } from "recoil";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import ButtonForModal from "components/ButtonForModal";
import {
  BuildingStorefrontIcon,
  FunnelIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/solid";
import MaterialExTable from "components/tables/MaterialExTable";
import DownloadButton from "components/DownloadButton";
import AddButton from "components/AddButton";
import {
  applianceAddState,
  applianceEditState,
  applianceOfficeId,
  applianceShowDetails,
  applianceViewDetails,
  isFilter,
  officeApplianceForEdit,
  pagination,
  showApplianceFromOffice,
} from "../atom";
import { OfficeApplianceViewDetails } from "./OfficeApplianceViewDetails";
import { OfficeApplianceForm } from "../form/OfficeApplianceForm";
import { useDeactivateOfficeAppliance } from "../hooks/useDeactivateOfficeAppliance";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";
import { useHistory, useLocation } from "react-router-dom";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import { OfficeTabs } from "modules/OfficeTab/OfficeTabs";
import { mainSelectedTab } from "modules/CarrierAgreementMgmt/atoms";
import { toast } from "components/toastNotification";
import { OfficeApplianceTableFilter } from "../filter/TableFilter";
import * as XLSX from "xlsx";

export const OfficeApplianceTable = () => {
  //#region Office Appliance Data
  const { data: officeAppliance, isLoading } = useGetOfficeAppliances();
  const deactivateOfficeAppliance = useDeactivateOfficeAppliance();

  //#region States
  const [isAddState, setIsAddState] = useRecoilState(applianceAddState);
  const [isEditState, setIsEditState] = useRecoilState(applianceEditState);
  const [selectedRowsToDelete, setSelectedRowsToDelete] = useState<any>();
  const [showModalToDelete, setShowModalToDelete] = useState(false);
  const [tableFilter, setTableFilter] = useState(false);
  const filterStatus = useRecoilValue(isFilter);
  const [rowForEdit, setRowForEdit] = useRecoilState(officeApplianceForEdit);
  const [rowViewDetails, setRowViewDetails] =
    useRecoilState(applianceViewDetails);
  const [showApplianceDetails, setShowApplianceDetails] =
    useRecoilState(applianceShowDetails);

  const [officeApplianceData, setOfficeApplianceData] = useState<any[]>([]);
  const [filteredOfficeAppliance, setFilteredOfficeAppliance] = useState<any[]>(
    []
  );
  const [officeApplianceId, setOfficeApplianceId] = useState("");
  const [officeId, setOfficeId] = useRecoilState(applianceOfficeId);
  const [exportOfficeAppliance, setExportOfficeAppliance] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadSuccess, setIsDownloadSuccess] = useState(false);
  const [page, setPage] = useRecoilState(pagination);
  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  const [showFromOffice, setShowApplianceFromOffice] = useRecoilState(
    showApplianceFromOffice
  );
  //#endregion
  const tableRef = useRef<any>();
  const history = useHistory();
  let location = useLocation();
  let recordIdFromRecent;

  if (location.state !== null && location.state !== undefined) {
    recordIdFromRecent = location.state;
  }

  //#region Permissions
  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission = PERMISSIONS.permission_officeappliance_add;
  const editPermission = PERMISSIONS.permission_officeappliance_edit;
  const deactivatePermission = PERMISSIONS.permission_officeappliance_delete;
  //#endregion

  //#region Office Appliance Data UseEffect
  useEffect(() => {
    if (officeAppliance) {
      setOfficeApplianceData(officeAppliance);
    }
  }, [officeAppliance]);
  //#endregion

  //#region PrevPage
  let prevPage = new URLSearchParams(globalThis.location.search).get(
    "prevPage"
  );
  useEffect(() => {
    if (prevPage !== null && isAddState) {
      setPage(page);
    } else if (prevPage !== null && isEditState) {
      setPage(page);
    } else if (prevPage !== null && showApplianceDetails) {
      setPage(page);
    } else if (prevPage !== null && tableRef.current !== Number(prevPage)) {
      tableRef.current = page;
    }
  });
  //#endregion

  //#region Selected Rows To Delete UseEffect
  useEffect(() => {
    setSelectedRowsToDelete([]);
  }, [setSelectedRowsToDelete]);
  //#endregion

  //#region From Recent Activity Redirection
  useEffect(() => {
    const loadFilteredData = async () => {
      if (!recordIdFromRecent) {
        setFilteredOfficeAppliance([]);
        localStorage.removeItem("NotifFilter");
        localStorage.removeItem("ModuleFilter");
        return;
      }
      try {
        const result = await notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        );

        setFilteredOfficeAppliance(result);
        setOfficeApplianceId(recordIdFromRecent.recordId);
        history.push("/officesettings/officeappliance");
      } catch (error) {
        console.error("Failed to load fitlered notification data:", error);
      }
    };
    loadFilteredData();
  }, []);

  useEffect(() => {
    if (officeApplianceId !== "" && officeApplianceId !== undefined) {
      const cleanIds = officeApplianceId?.replaceAll(".", ",");

      const idList = cleanIds?.split(",").map((x) => Number(x.trim()));

      const filtered = officeAppliance?.filter((item) =>
        idList?.includes(item.officeApplianceId)
      );

      setFilteredOfficeAppliance(filtered);
    }
  }, [officeApplianceId, officeAppliance]);
  //#endregion

  //#region Onclick Office Appliance
  const onclickAppliance = (appliance) => {
    setRowViewDetails(appliance);
    setShowApplianceDetails(true);
  };
  //#endregion

  //#region Onclick Retail Store
  const onclickRetailStore = (applianceDetails) => {
    const url = "/officesettings/officesetting";
    history.push({
      pathname: url,
      state: {
        pathname: url,
        officeId: applianceDetails.officeId,
        action: "OfficeApplianceTable",
      },
    });
    const userTabs: any = [];

    for (const tab of OfficeTabs) {
      if (userPermissions.includes(tab.permission)) {
        userTabs.push({ to: tab.to });
      }
    }

    const index = userTabs.findIndex((t) => t.to === url);
    setSelectedIndex(index);
  };
  //#endregion

  //#region Handle Link
  const handleLink = (moduleName) => {
    const url = `/officesettings/${moduleName}`;
    history.push(moduleName);
    const userTabs: any = [];

    for (const tab of OfficeTabs) {
      if (userPermissions.includes(tab.permission)) {
        userTabs.push({ to: tab.to });
      }
    }

    const index = userTabs.findIndex((t) => t.to === url);
    setSelectedIndex(index);
  };
  //#endregion

  //#region Cancel Show
  const onclickCancelShow = () => {
    if (showFromOffice) {
      setShowApplianceFromOffice(false);
      handleLink("officesetting");
    }
    setShowApplianceDetails(false);
    setIsEditState(false);
    setIsAddState(false);
    if (filteredOfficeAppliance?.length > 0) {
      setFilteredOfficeAppliance(
        filteredOfficeAppliance.map((data) => ({ ...data }))
      );
    }
    setOfficeApplianceData(officeAppliance.map((data) => ({ ...data })));
  };
  //#endregion

  //#region Handle Edit
  const handleEdit = (rowDetails) => {
    setRowForEdit({
      ...rowDetails,
    });
    setIsEditState(true);
  };
  //#endregion

  //#region Table Columns
  const alignCenter = "center" as
    | "center"
    | "inherit"
    | "justify"
    | "left"
    | "right"
    | undefined;

  const matColumns = [
    {
      title: "Record Number",
      field: "officeApplianceId",
      render: (rowdata) => {
        return (
          <div
            className="cursor-pointer"
            onClick={() => onclickAppliance(rowdata)}
          >
            <span className="blueAndBold underline">
              {"A-" + String(rowdata.officeApplianceId)?.padStart(4, "0")}
            </span>
            <br></br>
          </div>
        );
      },
      customFilterAndSearch: (term, rowData) =>
        ("A-" + String(rowData.officeApplianceId)?.padStart(4, "0"))
          .toLowerCase()
          .includes(term.toLowerCase()),
    },
    {
      title: "Type",
      field: "officeApplianceType",
    },
    {
      title: "Purchase Date",
      field: "purchaseDate",
      render: (rowData) => {
        const purchaseDate = rowData.purchaseDate
          ? new Date(rowData.purchaseDate).toLocaleDateString()
          : "";
        return <div>{purchaseDate}</div>;
      },
    },
    {
      title: "Purchase Order",
      field: "purchaseOrder",
    },
    {
      title: "Retail Store Number",
      field: "retailStoreNumber",
      render: (rowdata) => {
        return (
          <div
            className="cursor-pointer"
            onClick={() => onclickRetailStore(rowdata)}
          >
            <span className="blueAndBold underline">
              {rowdata.retailStoreNumber}
            </span>
            <br></br>
          </div>
        );
      },
    },
    {
      title: "Brand",
      field: "officeApplianceBrand",
    },
    {
      title: "Model Number",
      field: "modelNumber",
    },
    {
      title: "Part Number",
      field: "partNumber",
    },
    {
      title: "Item Price",
      field: "itemPrice",
      render: (rowData) => {
        return `$${rowData.itemPrice}`;
      },
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return <div>{rowData.isActive ? "Active" : "Inactive"}</div>;
      },
    },
    {
      title: "Action",
      align: alignCenter,
      render: (rowData) => {
        return userPermissions.includes(editPermission) ? (
          <div className="flex place-content-center mr-4">
            <ButtonForModal
              className={"border border-quaternary text-white bg-quaternary"}
              onClick={() => {
                handleEdit(rowData);
              }}
              isDisabled={false}
            >
              <PencilSquareIcon className="h-4 w-4" />
              Edit
            </ButtonForModal>
          </div>
        ) : (
          ""
        );
      },
    },
  ];
  //#endregion

  //#region Deactivate
  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRowsToDelete(forDelete);
  };

  const handleDelete = () => {
    const officeApplianceIds = selectedRowsToDelete
      ? selectedRowsToDelete.map((row) => row.officeApplianceId)
      : undefined;
    deactivateOfficeAppliance(officeApplianceIds);
    setSelectedRowsToDelete([]);
    setShowModalToDelete(false);
  };

  const applianceLabel = () => {
    if (!selectedRowsToDelete) return undefined;
    return selectedRowsToDelete?.length > 1
      ? "Office Appliances"
      : "Office Appliance";
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${
      selectedRowsToDelete?.length
    } ${applianceLabel()}?`}</div>
  );

  //#endregion

  //#region Download
  const exportOfficeAppliances = (exportData) => {
    if (exportData) {
      setExportOfficeAppliance(
        exportData.map((x) => {
          return {
            "Record Number": x.officeApplianceId,
            "Retail Store Number": x.retailStoreNumber,
            "Retail Store Address": x.retailStoreAddress,
            Type: x.officeApplianceType,
            Brand: x.officeApplianceBrand,
            "Purchase Date": x.purchaseDate
              ? new Date(x.purchaseDate)?.toLocaleDateString()
              : "",
            "Purchase Order": x.purchaseOrder,
            "Item Description": x.itemDescription,
            "Model Number": x.modelNumber,
            "Part Number": x.partNumber,
            "Quantity Ordered": x.quantityOrdered,
            "Item Price": x.itemPrice,
            Status: x.isActive ? "Active" : "Inactive",
            "Created By": x.createdBy,
            "Created Date": x.createdDate
              ? new Date(x.createdDate)?.toLocaleDateString()
              : "",
            "Modified By": x.modifiedBy,
            "Modified Date": x.modifiedDate
              ? new Date(x.modifiedDate)?.toLocaleDateString()
              : "",
          };
        })
      );
      setTimeout(() => {
        toast.fire(
          "Download Office Appliances",
          "Office Appliances downloaded successfully.",
          "success"
        );
        setIsDownloading(false);
        setIsDownloadSuccess(true);
      }, 2000);
    }
  };
  //#endregion

  //#region XLSX UseEffect
  useEffect(() => {
    if (isDownloadSuccess) {
      const officeAppliance = XLSX.utils.json_to_sheet(exportOfficeAppliance);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, officeAppliance, "Office Appliances");
      XLSX.writeFile(
        wb,
        "OfficeAppliances_" + new Date()?.toLocaleDateString() + ".xlsx"
      );
      setIsDownloadSuccess(false);
    }
  }, [isDownloadSuccess]);
  //#endregion

  //#region Filter From Office
  useEffect(() => {
    if (officeId) {
      setFilteredOfficeAppliance(
        officeAppliance
          ?.filter((x) => x.officeId === officeId)
          ?.map((data) => ({ ...data }))
      );
    }
  }, [officeAppliance, officeId]);

  // Reset officeId
  useEffect(() => {
    return () => {
      setOfficeId(null);
    };
  }, [setOfficeId]);
  //#endregion

  return (
    <div className="px-8 py-4 min-h-fitmin-h-fit">
      {isAddState || isEditState ? (
        <OfficeApplianceForm
          isAddState={isAddState}
          isEditState={isEditState}
          setIsAddState={setIsAddState}
          setIsEditState={setIsEditState}
          rowForEdit={rowForEdit}
          setRowForEdit={setRowForEdit}
        />
      ) : showApplianceDetails ? (
        <div className="relative py-2 box-shadow border-quinary border-l-2">
          <div className="">
            <OfficeApplianceViewDetails
              applianceDetails={rowViewDetails}
              handleEdit={handleEdit}
              onclickCancelShow={onclickCancelShow}
              setIsAddState={setIsAddState}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center ">
            <div className="flex gap-2 items-center">
              <BuildingStorefrontIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
              <div className="flex flex-col leading tight">
                <h3 className="text-xs font-medium text-gray-500">
                  Office Appliance Module
                </h3>
                <h1 className="mr-auto font-bold text-primary text-md uppercase">
                  ALL Office Appliance
                </h1>
              </div>
            </div>
          </div>

          <div className="flex flex-row justify-between py-2">
            <div className="text-secondary italic text-xs3 ">
              {userPermissions.includes(deactivatePermission) && (
                <div className="pb-2">
                  <p className="font-bold italic inline-block mr-1">Note: </p>
                  Tick the box to deactivate some data.
                </div>
              )}
              <div className="space-x-4 mr-6">
                <button
                  className="hover:scale-90 transition duration-500 flex justify-center items-center shadow-md px-3 py-1 h-7 text-xs 
                  gap-x-2 font-semibold text-primary border hover:bg-primary selected hover:text-white border-primary rounded-md"
                  onClick={() => {
                    setTableFilter(!tableFilter);
                  }}
                >
                  <div>Filter By</div>
                  <FunnelIcon className="h-3 w-3 focus:bg-primary" />
                </button>
              </div>
            </div>
            <div className="space-x-4 mr-6 mb-2"></div>
            <div className="gap-2 flex items-center">
              {userPermissions.includes(addPermission) && (
                <div>
                  <AddButton
                    className={"opacity-100"}
                    disabled={false}
                    buttonText="Add"
                    onClick={() => {
                      setIsAddState(true);
                    }}
                  />
                </div>
              )}

              <div className="box-content">
                <DownloadButton
                  disabled={isDownloading}
                  buttonValue={
                    isDownloading ? "Downloading..." : "Download File"
                  }
                  onClick={() => {
                    setIsDownloading(true);
                    exportOfficeAppliances(
                      tableRef &&
                        tableRef.current.dataManager.searchedData.length > 0
                        ? tableRef.current.dataManager.searchedData
                        : officeAppliance
                    );
                  }}
                />
              </div>
            </div>
          </div>
          <main className="flex justify-between">
            {tableFilter && (
              <div className="overflow-y-auto flex-col flex-grow bg-white">
                <div className="mb-4">
                  {!isAddState && !isEditState && !showApplianceDetails && (
                    <OfficeApplianceTableFilter
                      setFilteredOfficeAppliance={setFilteredOfficeAppliance}
                      tableFilter={tableFilter}
                    />
                  )}
                </div>
              </div>
            )}
            <div
              className={
                tableFilter
                  ? "w-4/5  mb-4 bg-white  overflow-x-auto box-shadow"
                  : "w-full mb-4 bg-white  overflow-x-auto box-shadow"
              }
            >
              <div className="grid grid-row">
                <MaterialExTable
                  columns={matColumns}
                  data={
                    filterStatus || filteredOfficeAppliance?.length > 0
                      ? filteredOfficeAppliance
                      : officeApplianceData
                  }
                  isLoading={isLoading}
                  search={true}
                  selectedRows={selectedRowsToDelete}
                  selection={userPermissions.includes(deactivatePermission)}
                  toolbar={true}
                  setOpenModal={setShowModalToDelete}
                  onSelectionChange={handleSelectedRowsChange}
                  actions={undefined}
                  editable={undefined}
                  exportButton={undefined}
                  grouping={undefined}
                  detailPanel={undefined}
                  toolBarComponent={undefined}
                  panelWidth={undefined}
                  tableRef={tableRef}
                  onSearchChange={undefined}
                  fileName={undefined}
                  title={undefined}
                />
                <ConfirmBoxDelete
                  ModalTitle="Deactivate Office Appliance"
                  qConfirmation={deleteMessage}
                  isOpen={showModalToDelete}
                  onClose={() => setShowModalToDelete(false)}
                  onClickFunction={handleDelete}
                  cancelButtonRef={undefined}
                />
              </div>
            </div>
          </main>
        </>
      )}
    </div>
  );
};
